import {
  createWebhook as createWebhookApi,
  deleteWebhook as deleteWebhookApi,
} from '@/api/webhook'
import { useRequiredOrgId } from '@/hooks/useRequiredOrgId'
import { queryKeys } from '@/lib/query-keys'
import type { Webhook } from '@server/types/webhookTypes.ts'
import { useMutation, useQueryClient } from '@tanstack/react-query'

export const useWebhookActions = () => {
  const queryClient = useQueryClient()
  const organizationId = useRequiredOrgId()
  const webhooksQueryKey = queryKeys.webhooks.all(organizationId)

  const createWebhook = useMutation({
    mutationFn: createWebhookApi,
    onSettled: () => {
      // Refetch webhooks after create completes
      void queryClient.invalidateQueries({ queryKey: webhooksQueryKey })
    },
  })

  const deleteWebhook = useMutation({
    mutationFn: deleteWebhookApi,
    onMutate: async ({ webhookId }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: webhooksQueryKey,
      })

      // Snapshot the previous value
      const previousWebhooks =
        queryClient.getQueryData<Webhook[]>(webhooksQueryKey)

      // Optimistically update by removing the deleted webhook
      if (previousWebhooks) {
        queryClient.setQueryData(
          webhooksQueryKey,
          previousWebhooks.filter((webhook) => webhook.id !== webhookId),
        )
      }

      // Return a context with the previous value
      return { previousWebhooks }
    },
    onError: (_err, _variables, context) => {
      // If the mutation fails, use the context from onMutate to roll back
      if (context?.previousWebhooks) {
        queryClient.setQueryData(webhooksQueryKey, context.previousWebhooks)
      }
    },
    onSettled: () => {
      // Always refetch after error or success to ensure fresh data
      void queryClient.invalidateQueries({
        queryKey: webhooksQueryKey,
      })
    },
  })

  return { createWebhook, deleteWebhook }
}
